import { useParams, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useInvitationDetails, useInvitationAcceptance } from "@/hooks/useProjectInvitations";
import { useAuth } from "@/hooks/use-auth";
import { Mail, UserPlus, Clock, AlertCircle, CheckCircle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

export default function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: invitation, isLoading, error } = useInvitationDetails(token || "");
  const { acceptInvitation, isAccepting } = useInvitationAcceptance();

  useEffect(() => {
    if (!user) {
      // Redirect to login with return URL
      navigate(`/auth?redirect=${encodeURIComponent(window.location.pathname)}`);
    }
  }, [user, navigate]);

  const handleAcceptInvitation = async () => {
    if (!token) return;
    
    try {
      const result = await acceptInvitation(token);
      // Redirect to the project page
      navigate(`/projects/${result.projectId}`);
    } catch (error) {
      // Error is handled by the hook
    }
  };

  if (!user) {
    return (
      <div className="container mx-auto py-8">
        <Card className="max-w-md mx-auto">
          <CardHeader className="text-center">
            <Mail className="w-12 h-12 mx-auto mb-4 text-primary" />
            <CardTitle>Sign In Required</CardTitle>
            <CardDescription>
              Please sign in to accept this project invitation.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => navigate(`/auth?redirect=${encodeURIComponent(window.location.pathname)}`)}
              className="w-full"
            >
              Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container mx-auto py-8">
        <Card className="max-w-md mx-auto">
          <CardContent className="text-center py-8">
            <LoadingSpinner className="mx-auto mb-4" />
            <p>Loading invitation details...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !invitation) {
    return (
      <div className="container mx-auto py-8">
        <Card className="max-w-md mx-auto">
          <CardHeader className="text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-destructive" />
            <CardTitle>Invitation Not Found</CardTitle>
            <CardDescription>
              This invitation link is invalid, expired, or has already been used.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => navigate("/projects")}
              variant="outline"
              className="w-full"
            >
              Go to Projects
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isExpired = invitation.expires_at < new Date();

  if (isExpired) {
    return (
      <div className="container mx-auto py-8">
        <Card className="max-w-md mx-auto">
          <CardHeader className="text-center">
            <Clock className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <CardTitle>Invitation Expired</CardTitle>
            <CardDescription>
              This invitation has expired. Please ask the project owner to send a new invitation.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center text-sm text-muted-foreground mb-4">
              Expired {formatDistanceToNow(invitation.expires_at, { addSuffix: true })}
            </div>
            <Button 
              onClick={() => navigate("/projects")}
              variant="outline"
              className="w-full"
            >
              Go to Projects
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <Card className="max-w-md mx-auto">
        <CardHeader className="text-center">
          <UserPlus className="w-12 h-12 mx-auto mb-4 text-primary" />
          <CardTitle>Project Invitation</CardTitle>
          <CardDescription>
            You've been invited to collaborate on a project.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <h3 className="font-semibold text-lg">{invitation.project_name}</h3>
            <p className="text-sm text-muted-foreground">
              <strong>{invitation.inviter_name}</strong> has invited you to join this project as a <strong>{invitation.role}</strong>.
            </p>
          </div>

          <Alert>
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>
              As a <strong>{invitation.role}</strong>, you will be able to:
              <ul className="list-disc list-inside mt-2 space-y-1">
                {invitation.role === 'admin' && (
                  <>
                    <li>Manage project settings and team members</li>
                    <li>View all time entries and analytics</li>
                    <li>Create invoices and export reports</li>
                  </>
                )}
                {invitation.role === 'member' && (
                  <>
                    <li>Add and edit time entries</li>
                    <li>View project analytics and reports</li>
                    <li>Collaborate with team members</li>
                  </>
                )}
                {invitation.role === 'viewer' && (
                  <>
                    <li>View project data and analytics</li>
                    <li>See team member activities</li>
                    <li>Access read-only reports</li>
                  </>
                )}
              </ul>
            </AlertDescription>
          </Alert>

          <div className="text-center text-sm text-muted-foreground">
            This invitation expires {formatDistanceToNow(invitation.expires_at, { addSuffix: true })}
          </div>

          <div className="flex gap-2">
            <Button 
              onClick={() => navigate("/projects")}
              variant="outline"
              className="flex-1"
            >
              Decline
            </Button>
            <Button 
              onClick={handleAcceptInvitation}
              className="flex-1"
              disabled={isAccepting}
            >
              {isAccepting ? "Accepting..." : "Accept Invitation"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}